import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { observer } from 'mobx-react';
import { compose } from 'recompose';
import format from 'string-template';
import * as Sentry from '@sentry/browser';

import TextEditor from '../../../../../components/TextEditor';
import ToDoList from '../../../ToDoList';
import User from '../../../../../Model/User';
import LoadingOverlay from '../../../LoadingOverlay';
import useToast from '../../../../hooks/useToast';
import useComponentMounted from '../../../../../hooks/useComponentMounted';
import useSessionStore from '../../../../../hooks/useSessionStore';
import LoggedInUserContext from '../../../../../context/LoggedInUserContext';
import { DateFormat } from '../../../../../utils/date';

import NotesSummarize from './components/NotesSummarize';
import {
  Container,
  ButtonRow,
  StyledButton,
  StyledTitle,
  MetadataLine,
  SectionContainer,
  TitleRow,
  NotesTitleRow,
  AutoSavesIndicator,
  AutoSavesDot,
  Description,
} from './styles';
import texts from './texts.json';

// Quill's HTML→Delta→HTML round-trip injects <p><br></p> before list
// elements. Stripping these keeps the saved content stable across reloads.
const normalizeHtml = (html) => (html || '').replace(/<p><br\s*\/?><\/p>(?=<ul|<ol)/gi, '');

const WORKOUT_PREFERENCES_AUTOSAVE_MS = 1000;

const Notes = ({ userDoc, coachId }) => {
  const { showToast } = useToast();
  const { isAdmin } = useSessionStore();
  const isComponentMountedRef = useComponentMounted();
  const { userDoc: loggedInUserDoc, isReady: isLoggedInUserReady } = useContext(LoggedInUserContext);
  const editorDisplayName = (loggedInUserDoc?.name || '').trim() || loggedInUserDoc?.email || '';

  // ── Notes (manual save) ─────────────────────────────────────────────
  const [notes, setNotes] = useState(normalizeHtml(userDoc.coachNotes || ''));
  const [notesIsDirty, setNotesIsDirty] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const notesEditorRef = useRef(null);

  useEffect(() => {
    if (!notesIsDirty) {
      setNotes(normalizeHtml(userDoc.coachNotes || ''));
    }
  }, [userDoc.coachNotes, notesIsDirty]);

  const handleNotesChange = useCallback((value) => {
    setNotes(value);
    setNotesIsDirty(true);
  }, []);

  const saveNotes = useCallback(async (html) => {
    setIsSaving(true);
    try {
      await userDoc.updateCoachNotes(normalizeHtml(html), editorDisplayName);
      if (isComponentMountedRef.current) setNotesIsDirty(false);
      return true;
    } catch (error) {
      Sentry.captureException(error);
      showToast(texts.notesSaveError, { error: true });
      return false;
    } finally {
      if (isComponentMountedRef.current) setIsSaving(false);
    }
  }, [userDoc, editorDisplayName, showToast, isComponentMountedRef]);

  const onSaveNotes = useCallback(async () => {
    if (await saveNotes(notes)) showToast(texts.savedSuccessfully);
  }, [saveNotes, notes, showToast]);

  // ── Workout Preferences (auto-save on debounce) ─────────────────────
  const [workoutPreferences, setWorkoutPreferences] = useState(
    normalizeHtml(userDoc.workoutPreferences || ''),
  );
  const [wpIsDirty, setWpIsDirty] = useState(false);
  const wpDebounceRef = useRef(null);

  useEffect(() => {
    if (!wpIsDirty) {
      setWorkoutPreferences(normalizeHtml(userDoc.workoutPreferences || ''));
    }
  }, [userDoc.workoutPreferences, wpIsDirty]);

  const handleWpChange = useCallback((value) => {
    setWorkoutPreferences(value);
    setWpIsDirty(true);
    if (wpDebounceRef.current) clearTimeout(wpDebounceRef.current);
    wpDebounceRef.current = setTimeout(async () => {
      try {
        await userDoc.updateWorkoutPreferences(normalizeHtml(value), editorDisplayName);
        if (isComponentMountedRef.current) setWpIsDirty(false);
      } catch (error) {
        Sentry.captureException(error);
        showToast(texts.workoutPreferencesSaveError, { error: true });
      }
    }, WORKOUT_PREFERENCES_AUTOSAVE_MS);
  }, [userDoc, editorDisplayName, showToast, isComponentMountedRef]);

  useEffect(() => () => {
    if (wpDebounceRef.current) clearTimeout(wpDebounceRef.current);
  }, []);

  // ── Metadata for the Notes editor ───────────────────────────────────
  const updatedAt = userDoc.coachNotesUpdatedAt;
  const updatedByName = userDoc.coachNotesUpdatedByName;
  const showMetadata = !!updatedAt;
  const editedAtDateTime = updatedAt
    ? moment(updatedAt).format(DateFormat.LOCAL_DATE_AND_TIME)
    : '';

  return (
    <>
      <Container>
        <NotesTitleRow>
          <StyledTitle>{texts.notes}</StyledTitle>
          <NotesSummarize editorRef={notesEditorRef} onReplaced={saveNotes} />
        </NotesTitleRow>
        <TextEditor ref={notesEditorRef} value={notes} onChange={handleNotesChange} />
        {showMetadata && (
          <MetadataLine>
            {isAdmin
              ? format(texts.lastEditedPrefix, { name: updatedByName, date: editedAtDateTime })
              : format(texts.lastEditedAtPrefix, { date: editedAtDateTime })}
          </MetadataLine>
        )}
        <ButtonRow>
          <StyledButton onClick={onSaveNotes} disabled={!isLoggedInUserReady}>
            {texts.save}
          </StyledButton>
        </ButtonRow>

        <SectionContainer>
          <TitleRow>
            <StyledTitle>{texts.workoutPreferencesTitle}</StyledTitle>
            <AutoSavesIndicator>
              <AutoSavesDot />
              {texts.workoutPreferencesAutoSaves}
            </AutoSavesIndicator>
          </TitleRow>
          <Description
            dangerouslySetInnerHTML={{ __html: texts.workoutPreferencesDescription }}
          />
          <TextEditor
            value={workoutPreferences}
            onChange={handleWpChange}
            placeholder={texts.workoutPreferencesPlaceholder}
          />
        </SectionContainer>

        <ToDoList clientId={userDoc.id} coachId={coachId} fromQAP />
      </Container>
      <LoadingOverlay isLoading={isSaving} />
    </>
  );
};

Notes.propTypes = {
  coachId: PropTypes.string.isRequired,
  userDoc: PropTypes.instanceOf(User),
};

Notes.defaultProps = {
  userDoc: null,
};

export default compose(observer)(Notes);
